import { useState, useEffect } from "react";
import { adminGetContracts, adminGetMandates } from "@/api";
import { FileText, Shield, CheckCircle, Clock, XCircle, Landmark, Smartphone, CreditCard, AlertTriangle } from "lucide-react";

export default function AdminContractsMandates() {
  const [tab, setTab] = useState("contracts");
  const [contracts, setContracts] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === "contracts") {
        const res = await adminGetContracts();
        setContracts(res.data.contracts || []);
      } else {
        const res = await adminGetMandates();
        setMandates(res.data.mandates || []);
      }
    } catch {} finally { setLoading(false); }
  };

  const StatusBadge = ({ status, accepted }) => {
    if (accepted === true) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>accepted
      </span>
    );
    if (accepted === false) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>pending
      </span>
    );
    const config = {
      pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' },
      active: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-400' },
      failed: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-400' },
    };
    const c = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text} border ${c.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>{status}
      </span>
    );
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  // Compute mandate stats
  const mandateStats = {
    total: mandates.length,
    active: mandates.filter(m => m.status === 'active').length,
    pending: mandates.filter(m => m.status === 'pending').length,
    upiAutopay: mandates.filter(m => m.razorpay_subscription_id || m.mandate_method === 'upi').length,
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-contracts-mandates-page">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Outfit, sans-serif'}}>Contracts & Mandates</h2>
        <p className="text-sm text-[#6B7280] mt-1">View and manage customer contracts and autopay mandates</p>
      </div>

      {/* Tab Switcher */}
      <div className="bg-white rounded-2xl border border-gray-100 p-1.5 shadow-sm inline-flex gap-1">
        {["contracts", "mandates"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`cm-tab-${t}`}
            className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
              tab === t
                ? 'bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white shadow-sm'
                : 'text-[#6B7280] hover:bg-gray-50 hover:text-[#0A2540]'
            }`}>
            {t === "contracts" ? <><FileText className="h-4 w-4" /> Contracts ({contracts.length || '...'})</> : <><Shield className="h-4 w-4" /> Mandates ({mandates.length || '...'})</>}
          </button>
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 lg:grid-cols-4 gap-4">
        {tab === 'contracts' ? (
          <>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Total Contracts</p><p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{contracts.length}</p></div>
                <div className="bg-blue-50 p-3 rounded-xl"><FileText className="h-6 w-6 text-blue-500" strokeWidth={1.8} /></div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Accepted</p><p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{contracts.filter(c => c.accepted_by_user).length}</p></div>
                <div className="bg-emerald-50 p-3 rounded-xl"><CheckCircle className="h-6 w-6 text-emerald-500" strokeWidth={1.8} /></div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Pending</p><p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{contracts.filter(c => !c.accepted_by_user).length}</p></div>
                <div className="bg-amber-50 p-3 rounded-xl"><Clock className="h-6 w-6 text-amber-500" strokeWidth={1.8} /></div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Total Mandates</p><p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{mandateStats.total}</p></div>
                <div className="bg-violet-50 p-3 rounded-xl"><Shield className="h-6 w-6 text-violet-500" strokeWidth={1.8} /></div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Active</p><p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{mandateStats.active}</p></div>
                <div className="bg-emerald-50 p-3 rounded-xl"><CheckCircle className="h-6 w-6 text-emerald-500" strokeWidth={1.8} /></div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Pending</p><p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{mandateStats.pending}</p></div>
                <div className="bg-amber-50 p-3 rounded-xl"><Clock className="h-6 w-6 text-amber-500" strokeWidth={1.8} /></div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">UPI Autopay</p><p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{mandateStats.upiAutopay}</p></div>
                <div className="bg-blue-50 p-3 rounded-xl"><Smartphone className="h-6 w-6 text-blue-500" strokeWidth={1.8} /></div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-3 border-[#1565C0]/20 border-t-[#1565C0] rounded-full animate-spin" style={{borderWidth: '3px'}} />
              <p className="text-sm text-[#6B7280]">Loading...</p>
            </div>
          </div>
        ) : tab === "contracts" ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-50/80 border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Customer</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Lender</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Amount</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Status</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Accepted At</th>
                </tr>
              </thead>
              <tbody>
                {contracts.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-16"><FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-[#6B7280] font-medium">No contracts yet</p></td></tr>
                ) : contracts.map(c => (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1565C0] to-[#00BCD4] flex items-center justify-center text-white font-bold text-sm shadow-sm">{(c.user?.name || 'U')[0].toUpperCase()}</div>
                        <div><p className="font-semibold text-[#0A2540]">{c.user?.name || '—'}</p><p className="text-xs text-[#6B7280]">{c.user?.mobile}</p></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#0A2540] font-medium">{c.application?.lender_name || '—'}</td>
                    <td className="px-6 py-4"><span className="text-lg font-black text-[#0A2540]" style={{fontFamily:'Outfit, sans-serif'}}>₹{Number(c.application?.loan_amount || 0).toLocaleString()}</span></td>
                    <td className="px-6 py-4"><StatusBadge accepted={c.accepted_by_user} /></td>
                    <td className="px-6 py-4 text-xs text-[#6B7280]">{formatDate(c.accepted_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-50/80 border-b border-gray-100">
                  <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Customer</th>
                  <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Method</th>
                  <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Razorpay Sub ID</th>
                  <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Status</th>
                  <th className="text-right px-5 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Per Instalment</th>
                  <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Next Charge</th>
                  <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Progress</th>
                  <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Setup At</th>
                </tr>
              </thead>
              <tbody>
                {mandates.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-16"><Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-[#6B7280] font-medium">No mandates yet</p></td></tr>
                ) : mandates.map(m => {
                  const method = m.razorpay_subscription_id ? 'UPI Autopay' : (m.mandate_type || 'eNACH');
                  const isUPI = method === 'UPI Autopay';

                  return (
                    <tr key={m.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors duration-150">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">{(m.user?.name || 'U')[0].toUpperCase()}</div>
                          <div>
                            <p className="font-semibold text-[#0A2540] text-sm">{m.user?.name || '—'}</p>
                            <p className="text-[10px] text-[#6B7280]">{m.user?.mobile}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        {isUPI ? (
                          <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-blue-100">
                            <Smartphone className="h-3 w-3" /> UPI Autopay
                          </span>
                        ) : (
                          <code className="bg-gray-50 text-[#475569] px-2.5 py-1 rounded-lg text-xs font-mono font-bold border border-gray-200">{method}</code>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {m.razorpay_subscription_id ? (
                          <code className="text-[10px] font-mono text-[#6B7280] bg-gray-50 px-2 py-1 rounded border border-gray-100">
                            {m.razorpay_subscription_id.substring(0, 16)}...
                          </code>
                        ) : <span className="text-xs text-[#D1D5DB]">—</span>}
                      </td>
                      <td className="px-5 py-4"><StatusBadge status={m.status} /></td>
                      <td className="px-5 py-4 text-right">
                        <span className="text-sm font-black text-[#0A2540]" style={{fontFamily:'Outfit, sans-serif'}}>
                          ₹{Number(m.per_installment_amount || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-[#6B7280]">
                        {m.next_charge_date ? formatDate(m.next_charge_date) : '—'}
                      </td>
                      <td className="px-5 py-4">
                        {m.installment_count ? (
                          <span className="text-xs font-semibold text-[#0A2540]">
                            {m.emis_paid || 0} of {m.emis_total || m.installment_count}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-5 py-4 text-xs text-[#6B7280]">{formatDate(m.setup_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
